/**
 * Types for the metric prompt optimization flow
 */

export type ChangeType =
  | "clarity"
  | "edge_case"
  | "structure"
  | "example"
  | "logic"
  | "general"

export interface Change {
  id: string
  type: ChangeType
  location: string
  originalText: string
  newText: string
  explanation: string
  basedOnFeedback?: string[]
  confidence: number
}

export interface AnalysisResult {
  criteriaIssues: string[]
  logicIssues: string[]
  missingEdgeCases: string[]
  exampleGaps: string[]
}

export interface OptimizationResult {
  analysis?: AnalysisResult
  changes: Change[]
  improvedMetric: string
  explanation: string
}

export type ImprovementFocus =
  | "general"
  | "feedback"
  | "edge_cases"
  | "examples"

export interface ImprovementConfig {
  selectedAnnotationIds: string[]
  improvementFocus: ImprovementFocus
  includeDisagreementAnalysis: boolean
}

export interface DisagreementHotspot {
  condition: string
  agreementRate: number
  annotationCount: number
}

export interface FeedbackTheme {
  theme: string
  count: number
  annotationIds: string[]
}

export type MachineComparisonStatus =
  | "agreement"
  | "disagreement"
  | "not_comparable"

export interface AnnotationMachineComparison {
  status: MachineComparisonStatus
  matchingUnits: number
  comparableUnits: number
  humanValue: string
  machineValue: string
}

export interface DisagreementAnalysis {
  machineDisagreementRate: number
  machineAgreements: number
  machineDisagreements: number
  comparableUnits: number
  nonComparableAnnotations: number
  comparisonsByAnnotationId: Record<string, AnnotationMachineComparison>
  hotspots: DisagreementHotspot[]
  themes: FeedbackTheme[]
}

export const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  clarity: "Clarity",
  edge_case: "Edge Case",
  structure: "Structure",
  example: "Example",
  logic: "Logic",
  general: "General",
}

export const CHANGE_TYPE_COLORS: Record<
  ChangeType,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  clarity: "info",
  edge_case: "warning",
  structure: "default",
  example: "success",
  logic: "danger",
  general: "default",
}
